import { CSSProperties, ReactNode, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { TEXT_COLOR } from "../../lib/style_definitions";
import { useModalAction } from "../../lib/hooks/useModalAction";

interface MButtonProps {
  children?: ReactNode;
  icon?: ReactNode;
  onClick?: () => void | Promise<void>;
  type?: "primary" | "secondary" | "white" | "ghost" | "danger";
  disabled?: boolean;
  loading?: boolean;
  style?: CSSProperties;
}

const base: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  height: 44,
  padding: "0 14px",
  borderRadius: 12,
  fontSize: 16,
  fontWeight: 600,
  border: "none",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  transition: "transform 0.15s, box-shadow 0.15s, opacity 0.15s",
  lineHeight: 1.2,
};

const types: Record<string, CSSProperties> = {
  primary: {
    background: "linear-gradient(135deg, #60a5fa, #3b82f6)",
    color: "white",
  },
  secondary: {
    background: "#f3f4f6",
    color: TEXT_COLOR,
    boxShadow: "none",
  },
  white: {
    background: "white",
    color: TEXT_COLOR,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
  },
  ghost: {
    background: "transparent",
    color: "#6b7280",
    boxShadow: "none",
  },
  danger: {
    background: "linear-gradient(135deg, #ff6b6b, #ef4444)",
    color: "white",
  },
};

export function MButton({
  children,
  icon,
  onClick,
  type = "secondary",
  disabled,
  loading: externalLoading,
  style,
}: MButtonProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = externalLoading ?? internalLoading;
  const pending = internalLoading || !!externalLoading;
  const { isBusy: modalBusy, beginAction } = useModalAction(pending);
  const isDisabled = !!disabled || pending || modalBusy;
  const typeStyle = types[type] || types.secondary;

  async function handleClick() {
    if (!onClick || isDisabled) {
      return;
    }
    const result = onClick();
    if (result instanceof Promise) {
      beginAction();
      setInternalLoading(true);
      try {
        await result;
      } finally {
        setInternalLoading(false);
      }
    }
  }

  return (
    <button
      style={{
        ...base,
        ...typeStyle,
        ...(isDisabled && !loading ? { opacity: 0.4, cursor: "default" } : {}),
        ...style,
      }}
      disabled={isDisabled}
      onClick={handleClick}
    >
      {loading ? (
        <>
          <LoadingOutlined style={{ fontSize: 22, position: "absolute" }} />
          <span style={{ visibility: "hidden", display: "contents" }}>
            {icon}
            {children}
          </span>
        </>
      ) : (
        <>
          {icon && (
            <span style={{ fontSize: 20, display: "flex" }}>{icon}</span>
          )}
          {children}
        </>
      )}
    </button>
  );
}
